<template>
  <div class="app">
    <notifications
      class="vue-notifications"
      position="top right"
      :duration="5000"
      :speed="100"
    >
      <template #body="props">
        <base-notification
          :type="props.item.type"
          :title="props.item.title"
          :content="props.item.text"
          :close="props.close"
        />
      </template>
    </notifications>
    <div class="driver-info__container">
      <base-modal :show="confirmCancel && !!$route.meta.checkBeforeRedirect">
        <div slot="title">
          Deseja continuar?
        </div>

        <div slot="content">
          É possível que as alterações feitas não sejam salvas.
        </div>

        <div
          slot="footer"
          style="display: flex"
        >
          <base-button
            label="CONTINUAR NA PÁGINA"
            flat
            @on-click="() => (confirmCancel = false)"
          />
          <base-button
            label="DESCARTAR ALTERAÇÕES"
            type="warning"
            @on-click="() => onClickSidebar(item)"
          />
        </div>
      </base-modal>
      <base-modal
        :show="showFranchiseModalControl"
        @on-close="onClickCloseFranchise"
      >
        <div slot="title">
          Selecione a franquia em que deseja atuar
        </div>
        <div slot="subtitle">
          As alterações realizadas afetarão a franquia selecionada
        </div>
        <div slot="content">
          <div class="driver-info__container-space-between">
            <div>Franquia</div>
            <base-select
              blank-title="Selecione a franquia"
              :options="selectableFranchises"
              style="margin-top: 8px"
              @on-change="(value) => (selectedFranchise = value.value)"
            />
          </div>
        </div>
        <base-button
          slot="footer"
          type="warning"
          label="CONFIRMAR"
          :disabled="!selectedFranchise"
          @on-click="updateSelectedFranchise"
        />
      </base-modal>
    </div>
    <div class="app__body">
      <div class="app__body-nav">
        <BaseNav
          :page-name="pageName"
          :app-name="appName"
          :user-picture="actualUser.picture"
          :user-name="actualUser.name"
          :has-sidebar="hasSidebar"
          @toggle-franchise-modal="() => (showFranchiseModal = true)"
          @toggle-open-sidebar="() => (hasSidebar = true)"
          @toggle-close-sidebar="() => (hasSidebar = false)"
        />
      </div>
      <div class="app__body-content">
        <transition name="fade">
          <div 
            v-show="showSidebar && hasSidebar"
            class="app__sidebar"
          >
            <BaseSidebar
              :items="menuItems"
              :app-version="appVersion"            
              @on-click="onClickSidebar"
            />
          </div>
        </transition>
        <router-view :key="$route.fullPath" />
      </div>
    </div>
    <!-- Drawer -->
    <!--
      Requer que a rota atual contenha um componente em meta.drawerComponent
      Show é alterado e props são passados através do
      eventBus.$emit('toggle-drawer', ..args)
    -->
    <base-drawer
      :show="drawer.show && !!$route.meta.drawerComponent"
      @close="toggleDrawer(false)"
    >
      <component
        :is="$route.meta.drawerComponent"
        v-bind="{ ...drawer.props }"
        @close="toggleDrawer(false)"
      />
    </base-drawer>
    <!-- End drawer -->
  </div>
</template>

<script>
import { mapActions, mapGetters, } from 'vuex';
import BaseNotification from '../src/components/BaseNotification';
import eventBus from '@/plugins/eventBus';
import FacebookPixel from '@/mixins/FacebookPixel';

export default {
  name: 'App',
  components: {
    BaseNotification,
  },
  mixins: [
    FacebookPixel,
  ],
  data () {
    return {
      showFranchiseModal: false,
      showOnboardingStoreModal: false,
      selectedFranchise: '',
      confirmCancel: false,
      item: {},
      drawer: {
        show: false,
        props: null,
      },
      hasSidebar: true,
    };
  },
  computed: {
    ...mapGetters({
      appName: 'application/name',
      appVersion: 'application/version',
      actualUser: 'user/user',
      userFranchises: 'user/franchises',
      userSawOnboardingModal: 'user/getUserSawNewFeatureModal',
      isUserLogged: 'user/isLogged',
    },),
    selectableFranchises () {
      if (!Array.isArray(this.userFranchises,) || !this.userFranchises?.length) {
        return [];
      }
      return this.userFranchises.map((franchise,) => ({
        text: franchise?.name || 'Sem nome',
        value: franchise?.id,
      }),);
    },
    hasCouponPermissions () {
      return this.actualUser?.permissions?.some((permission,) => permission.name.match('COUPON',),);
    },
    hasCampaignPermissions () {
      return this.actualUser?.permissions?.some((permission,) => permission.name.match('_CAMPAIGN',),);
    },
    hasFranchisePermissions () {
      return this.actualUser?.businessType == 'Franqueadora';
    },
    hasOccurrencePermissions () {
      return this.actualUser?.permissions?.some((permission,) => permission.name.match('OCCURRENCE',),);
    },
    hasFinancialStatusPermissions () {
      return this.actualUser?.permissions?.some((permission,) => permission.name.match('_FINANCIAL_STATUS',),);
    },
    hasUserPermissions () {
      return this.actualUser?.permissions?.some((permission,) => permission.name.match('USER',),);
    },
    hasStorePermissions () {
      return this.actualUser?.permissions?.some((permission,) => permission.name.match('STORE',),);
    },
    hasNotificationPermissions () {
      return this.actualUser?.permissions?.some((permission,) => permission.name.match('NOTIFICATION',),);
    },
    hasDeeplinksPermissions () {
      return this.actualUser?.businessType == 'Franqueadora';
    },
    menuItems () {
      const filteredRoutes = this.filterRoutesByPermissions(
        this.$router?.options?.routes || [],);
      const sectionsShown = new Set();
      
      return filteredRoutes.filter((route,) => route.meta?.isAvaliableInMenu,).
        map((route,) => {
          const section = route.meta?.menuData?.section;
          const isFirstInSection = section && !sectionsShown.has(section,);
          
          if (isFirstInSection) {
            sectionsShown.add(section,);
          }
          
          return {
            ...route,
            active: this.$route.path.startsWith(route.path,),
            displaySection: isFirstInSection,
            businessType: this.actualUser?.businessType,
          };
        },);
    },
    pageName () {
      if (typeof this.$route?.meta?.navbarTitle === 'function') {
        return this.$route.meta.navbarTitle();
      }
      return this.$route?.meta?.navbarTitle || '';
    },
    showFranchiseModalControl () {
      return this.showFranchiseModal;
    },
  },
  watch: {
    '$route.meta.showSidebar': {
      immediate: true,
      handler (show,) {
        if (show === false) {
          this.showSidebar = false;
        } else {
          this.showSidebar = true;
        }
      },
    },
    '$route.name': {
      immediate: true,
      handler (routeName,) {
        if (routeName === 'list-stores' && this.userFranchises?.[0]?.id) {
          const franchiseId = this.userFranchises[0].id;
          this.selectedFranchise = franchiseId;
          
          this.$store.dispatch('user/setCurrentFranchise', franchiseId,);
          this.$store.dispatch('user/setBaseFranchise', franchiseId,);
        }
      },
    },
  },
  created () {
    window.addEventListener('beforeunload', this.renewToken(),);
    /* Exibe confirmação de saída da tela */
    eventBus.$on('show-cancel-confirmation', (item,) => {
      this.onClickSidebar(item,);
    },);
    /* Exibe confirmação de saída da tela */
    eventBus.$on('toggle-drawer', ([value, props,],) =>
      this.toggleDrawer(value, props,),
    );
  },
  mounted () {
    Promise.all([
      this.$store.dispatch('application/get',).catch((err,) => {
        console.warn('[APP] Failed to load application data:', err.message,);
        return null;
      },),
      this.$store.dispatch('application/getStates',).catch((err,) => {
        console.warn('[APP] Failed to load states:', err.message,);
        return null;
      },),
    ],).
      then(() => {
        console.log('[APP] MOUNTED',);
      },).
      catch((err,) => {
        console.error('[APP] Error during mount:', err,);
      },).
      finally(() => this.showOnboardingStoreModal = true,);
  },
  methods: {
    ...mapActions({
      setOnboardingModalUser: 'user/setOnboardingModalUser',
    },),
    renewToken () {
      if (
        !this.$auth.renewToken() ||
        (this.$auth.isAuthenticated() && !this.$auth.isValidToken())
      ) {
        return;
      }
      this.$auth.renewToken().then((data,) => {
        this.$store.dispatch('user/setUser', data,);
      },);
    },
    onClickSidebar (item,) {
      if (!item || !item.path) {
        return;
      }
      /* Refatorar pra usar o router guard e slot */
      if (this.confirmCancel ||
        !this.$route?.name?.includes('-create',) ||
        this.$route?.meta?.checkBeforeRedirect
      ) {
        this.confirmCancel = false;
        // eslint-disable-next-line no-unused-vars
        this.$router.push(item.path,).catch((err,) => { },);
      } else if (this.$route?.name?.includes('-create',) && !this.confirmCancel) {
        this.confirmCancel = true;
        this.item = item;
      }
    },
    onClickCloseFranchise () {
      if (this.$store.getters['user/currentFranchise']) {
        this.showFranchiseModal = false;
        this.selectedFranchise = '';
      }
    },
    async updateSelectedFranchise () {
      if (!this.selectedFranchise) {
        return;
      }
      
      await this.$store.dispatch(
        'user/setBaseFranchise',
        this.selectedFranchise,
      );
      await this.$store.dispatch(
        'user/setCurrentFranchise',
        this.selectedFranchise,
      );
      this.selectedFranchise = '';
      if (this.showFranchiseModalControl) { this.showFranchiseModal = false; }
      if (this.$route?.name?.includes('list-',)) {
        this.$router.go(this.$router.currentRoute,);
      }
    },
    /* Ativa/desativa a gaveta */
    toggleDrawer (newValue, props,) {
      this.drawer.props = props;
      /* Troca o estado da drawer */
      this.drawer.show = newValue;
    },

    filterRoutesByPermissions (routes,) {
      if (!routes || !Array.isArray(routes,)) {
        return [];
      }

      const permissionFilters = [
        { hasPermission: this.hasCouponPermissions, keyword: 'coupon', },
        /* Much+ inherits the same gate as the old "Pacote de cupons" item. */
        { hasPermission: this.hasCouponPermissions, keyword: 'much-plus', },
        { hasPermission: this.hasCampaignPermissions, keyword: 'campaign', },
        { hasPermission: this.hasFranchisePermissions, keyword: 'franchise', },
        { hasPermission: this.hasOccurrencePermissions, keyword: 'occurrence', },
        { hasPermission: this.hasFinancialStatusPermissions, keyword: 'financial-status', },
        { hasPermission: this.hasUserPermissions, keyword: 'user', },
        { hasPermission: this.hasNotificationPermissions, keyword: 'notification', },
        { hasPermission: this.hasDeeplinksPermissions, keyword: 'deeplinks', },
      ];

      let filteredRoutes = [...routes,];

      permissionFilters.forEach(({ hasPermission, keyword, },) => {
        if (!hasPermission) {
          filteredRoutes = filteredRoutes.filter(
            (route,) => !route.name?.includes(keyword,),
          );
        }
      },);

      if (!this.hasStorePermissions || !this.userFranchises?.length) {
        filteredRoutes = filteredRoutes.filter(
          (route,) => !route.name?.includes('store',),
        );
      }

      return filteredRoutes;
    },
  },
};
</script>
